'use client';
import { type ComponentProps, createContext, useContext, useState } from 'react';
import {
    Root,
    Trigger,
    Portal,
    Close,
    Overlay,
    Content,
    Title,
    Description,
} from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import { focusFirstField } from '@/util/focus';
import { cn } from '@/util/utils';

/*
  The banded content dialog (ALS-272): the prototype's customer-detail and lookbook dialogs put
  the title and the actions on muted bands with hairlines over a white body, and the close
  button in the head. DialogContent tells its header and footer through this.
*/
const DialogBandContext = createContext<{ banded: boolean; close: string | null }>({
    banded: false,
    close: null,
});

/**
 * A form in a dialog can lose its typing to Escape, the X or the backdrop as easily as one in a
 * drawer, so `dirty` asks first, with Sheet's own question, and `busy` holds the dialog open while
 * a save is on its way (ALS-406). A dialog that passes neither closes as it always has.
 */
function Dialog({
    open,
    defaultOpen,
    modal,
    dirty = false,
    busy = false,
    onOpenChange,
    children,
    ...props
}: Omit<ComponentProps<typeof Root>, 'onOpenChange'> & {
    onOpenChange?: (open: boolean) => void;
    dirty?: boolean;
    busy?: boolean;
}) {
    const [discardOpen, setDiscardOpen] = useState(false);
    return (
        <Root
            data-slot="dialog"
            open={open}
            defaultOpen={defaultOpen}
            modal={modal}
            onOpenChange={(next) => {
                if (busy) return;
                if (!next && dirty) setDiscardOpen(true);
                else onOpenChange?.(next);
            }}
            {...props}
        >
            {children}
            {dirty ? (
                <ConfirmDialog
                    open={discardOpen}
                    onOpenChange={setDiscardOpen}
                    title="Discard unsaved changes?"
                    description="Your changes have not been saved. Keep editing or discard them."
                    confirmLabel="Discard changes"
                    cancelLabel="Keep editing"
                    destructive
                    pending={busy}
                    onConfirm={() => {
                        setDiscardOpen(false);
                        onOpenChange?.(false);
                    }}
                />
            ) : null}
        </Root>
    );
}

function DialogTrigger({ asChild, ref, ...props }: ComponentProps<typeof Trigger>) {
    return <Trigger data-slot="dialog-trigger" asChild={asChild} ref={ref} {...props} />;
}

function DialogPortal({ container, forceMount, ...props }: ComponentProps<typeof Portal>) {
    return (
        <Portal
            data-slot="dialog-portal"
            container={container}
            forceMount={forceMount}
            {...props}
        />
    );
}

function DialogClose({ ref, asChild, ...props }: ComponentProps<typeof Close>) {
    return <Close data-slot="dialog-close" ref={ref} asChild={asChild} {...props} />;
}

function DialogOverlay({
    className,
    forceMount,
    ref,
    asChild,
    ...props
}: ComponentProps<typeof Overlay>) {
    return (
        <Overlay
            data-slot="dialog-overlay"
            forceMount={forceMount}
            ref={ref}
            asChild={asChild}
            className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
                className,
            )}
            {...props}
        />
    );
}

interface DialogContentProps extends ComponentProps<typeof Content> {
    showCloseButton?: boolean;
    /**
     * The corner X's spoken name. Give it one wherever the dialog's own footer already carries
     * a control called Close, so a screen reader is not offered two buttons of the same name
     * with no way to tell them apart (ALS-188).
     */
    closeLabel?: string;
    /**
     * The content dialog rather than the confirm box: DialogHeader and DialogFooter become the
     * muted bands, the close button moves into the header, and a DialogBody scrolls between.
     */
    banded?: boolean;
}

function DialogContent({
    className,
    children,
    showCloseButton = true,
    closeLabel = 'Close',
    banded = false,
    forceMount,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    asChild,
    ref,
    ...props
}: DialogContentProps) {
    return (
        <DialogPortal data-slot="dialog-portal">
            <DialogOverlay />
            <Content
                data-slot="dialog-content"
                forceMount={forceMount}
                onOpenAutoFocus={(event) => {
                    onOpenAutoFocus?.(event);
                    if (banded) focusFirstField(event);
                }}
                onCloseAutoFocus={onCloseAutoFocus}
                onEscapeKeyDown={onEscapeKeyDown}
                onPointerDownOutside={onPointerDownOutside}
                onFocusOutside={onFocusOutside}
                onInteractOutside={onInteractOutside}
                asChild={asChild}
                ref={ref}
                /*
                  The width is ONE class, not two layers of them. The copied library's own
                  `sm:max-w-lg` used to sit here under our `max-w-none`, and a responsive
                  variant is a different group to tailwind-merge, so it survived and capped
                  every dialog at 512px from the `sm` breakpoint up. Our own 420px default is
                  narrower, so nothing looked wrong until a caller asked for a wider one
                  (ALS-50's 900px session dialog) and silently got 512.
                */
                className={cn(
                    // No border: the prototype's confirm box is the card on its shadow alone.
                    'bg-card data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%] gap-2 rounded-lg p-6 shadow-lg duration-200',
                    'w-[min(420px,calc(100vw-48px))] max-w-none',
                    banded && 'flex max-h-[calc(100dvh-48px)] flex-col gap-0 overflow-hidden p-0',
                    className,
                )}
                {...props}
            >
                <DialogBandContext.Provider
                    value={{ banded, close: showCloseButton ? closeLabel : null }}
                >
                    {children}
                </DialogBandContext.Provider>
                {showCloseButton && !banded && (
                    // The X stays 16px; on touch an invisible 44px target is centred on it, inside
                    // the box's 16px corner inset, as the record header's Back arrow has (ALS-294).
                    <Close
                        data-slot="dialog-close"
                        className="ring-offset-background data-[state=open]:bg-accent data-[state=open]:text-muted-foreground focus-visible:border-ring absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus-visible:border-2 disabled:pointer-events-none pointer-coarse:after:absolute pointer-coarse:after:-inset-[14px] pointer-coarse:after:content-[''] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">{closeLabel}</span>
                    </Close>
                )}
            </Content>
        </DialogPortal>
    );
}

function DialogHeader({ className, children, ref, ...props }: ComponentProps<'div'>) {
    const { banded, close } = useContext(DialogBandContext);
    if (banded) {
        return (
            <div
                data-slot="dialog-header"
                data-banded
                className={cn(
                    'bg-muted border-border flex shrink-0 items-center gap-3 border-b px-6 py-4',
                    className,
                )}
                ref={ref}
                {...props}
            >
                <div className="flex min-w-0 flex-1 flex-col gap-0.5 [&_[data-slot=dialog-description]]:text-xs">
                    {children}
                </div>
                {close !== null ? (
                    <Close asChild>
                        <Button
                            data-slot="dialog-close"
                            data-close-button
                            variant="ghost"
                            size="icon"
                            aria-label={close}
                        >
                            <X className="size-[15px]" />
                        </Button>
                    </Close>
                ) : null}
            </div>
        );
    }
    return (
        <div
            data-slot="dialog-header"
            className={cn('flex flex-col gap-1.5 text-center sm:text-left', className)}
            ref={ref}
            {...props}
        >
            {children}
        </div>
    );
}

/** The banded dialog's white middle, the part that scrolls. */
function DialogBody({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="dialog-body"
            className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-5', className)}
            ref={ref}
            {...props}
        />
    );
}

function DialogFooter({ className, ref, ...props }: ComponentProps<'div'>) {
    const { banded } = useContext(DialogBandContext);
    return (
        <div
            data-slot="dialog-footer"
            data-banded={banded || undefined}
            className={cn(
                'mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
                banded &&
                    'bg-muted border-border mt-0 shrink-0 flex-wrap border-t px-6 py-4 sm:items-center',
                className,
            )}
            ref={ref}
            {...props}
        />
    );
}

function DialogTitle({ className, ref, asChild, ...props }: ComponentProps<typeof Title>) {
    return (
        <Title
            data-slot="dialog-title"
            ref={ref}
            asChild={asChild}
            className={cn('text-section-title text-foreground', className)}
            {...props}
        />
    );
}

function DialogDescription({
    className,
    ref,
    asChild,
    ...props
}: ComponentProps<typeof Description>) {
    return (
        <Description
            data-slot="dialog-description"
            ref={ref}
            asChild={asChild}
            className={cn('text-muted-foreground text-sm font-normal tracking-normal', className)}
            {...props}
        />
    );
}

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogTrigger,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
